import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import pick from 'lodash/pick';
import {
  createAlgoliaData,
  crudTransactions,
  initiatePrivileged,
  onUpdateEventBidListing,
  transitionPrivileged,
  updateAlgoliaData,
} from '../../util/api';
import { denormalisedResponseEntities, ensureOwnListing, getDistanceOfUser } from '../../util/data';
import { storableError } from '../../util/errors';
import * as log from '../../util/log';
import { types as sdkTypes } from '../../util/sdkLoader';
import { EVENT_BID_CONFIRMED } from '../../util/types';

import { getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { fetchCurrentUser, setCurrentUserHasOrders, fetchCurrentUserHasOrdersSuccess } from '../../ducks/user.duck';

const { UUID } = sdkTypes;

// ================ Async thunks ================ //

////////////////////
// Initiate Order //
////////////////////
const initiateOrderPayloadCreator = (
  { orderParams, processAlias, transactionId, transitionName, isPrivilegedTransition },
  { dispatch, extra: sdk, rejectWithValue }
) => {
  const initiateOrderParams = {
    orderParams, processAlias, transactionId, transitionName, isPrivilegedTransition
  }
  // If we already have a transaction ID, we should transition, not initiate.
  console.log(initiateOrderParams, ' ----initiateOrderParams')
  const isTransition = !!transactionId;
  if (!transitionName) {
    const error = new Error('Missing transition name for initiate/transition order request.');
    return rejectWithValue(storableError(error));
  }
  console.log("orderParams > ", orderParams)
  console.log("processAlias > ", processAlias)
  console.log("transactionId > ", transactionId)
  console.log("transitionName > ", transitionName)
  console.log("isPrivilegedTransition > ", isPrivilegedTransition)
  console.log("isTransition > ", isTransition)


  const { deliveryMethod, quantity, bookingDates, acceptBid, bidData, ...otherOrderParams } = orderParams;
  const quantityMaybe = quantity ? { stockReservationQuantity: quantity } : {};
  const bookingParamsMaybe = bookingDates || {};

  // Parameters only for client app's server
  const orderData = {
    ...(deliveryMethod ? { deliveryMethod } : {}),
    acceptBid,
    bidData,
    quantity,
    ...(otherOrderParams?.protectedData?.eventStartDateUnix ? { eventStartDateUnix: otherOrderParams.protectedData.eventStartDateUnix } : {})
  };

  // Parameters for Marketplace API
  const transitionParams = {
    ...quantityMaybe,
    ...bookingParamsMaybe,
    ...otherOrderParams,
  };

  const bodyParams = isTransition
    ? {
      id: transactionId,
      transition: transitionName,
      params: transitionParams,
    }
    : {
      processAlias,
      transition: transitionName,
      params: transitionParams,
    };
  const queryParams = {
    include: ['booking', 'provider'],
    expand: true,
  };

  console.log("BODY PARAMS", bodyParams)
  // return

  const handleSuccess = response => {
    const entities = denormalisedResponseEntities(response);
    const order = entities[0];
    dispatch(setCurrentUserHasOrders());
    return order;
  };

  const handleError = e => {
    const transactionIdMaybe = transactionId ? { transactionId: transactionId.uuid } : {};
    log.error(e, 'initiate-order-failed', {
      ...transactionIdMaybe,
      listingId: orderParams.listingId.uuid,
      ...quantityMaybe,
      ...bookingParamsMaybe,
      ...orderData,
      statusText: e.statusText,
    });
    return rejectWithValue(storableError(e));
  };


  if (isTransition && isPrivilegedTransition) {
    // transition privileged
    return transitionPrivileged({ isSpeculative: false, orderData, bodyParams, queryParams })
      .then(handleSuccess)
      .catch(handleError);
  } else if (isTransition) {
    // transition non-privileged
    return sdk.transactions
      .transition(bodyParams, queryParams)
      .then(handleSuccess)
      .catch(handleError);
  } else if (isPrivilegedTransition) {
    // initiate privileged
    return initiatePrivileged({ isSpeculative: false, orderData, bodyParams, queryParams })
      .then(handleSuccess)
      .catch(handleError);
  } else {
    // initiate non-privileged
    return sdk.transactions
      .initiate(bodyParams, queryParams)
      .then(handleSuccess)
      .catch(handleError);
  }
};

export const initiateOrderThunk = createAsyncThunk(
  'CheckoutPage/initiateOrder',
  initiateOrderPayloadCreator
);
// Backward compatible wrapper function for initiateOrder
export const initiateOrder = (
  orderParams,
  processAlias,
  transactionId,
  transitionName,
  isPrivilegedTransition
) => dispatch => {
  return dispatch(
    initiateOrderThunk({
      orderParams,
      processAlias,
      transactionId,
      transitionName,
      isPrivilegedTransition,
    })
  ).unwrap();
};

/////////////////////
// Confirm Payment //
/////////////////////
const confirmPaymentPayloadCreator = (
  { transactionId, transitionName, transitionParams = {} },
  { extra: sdk, rejectWithValue }
) => {
  const bodyParams = {
    id: transactionId,
    transition: transitionName,
    params: transitionParams,
  };
  const queryParams = {
    include: ['booking', 'provider', 'customer'],
    expand: true,
  };

  // confirm-invoice-payment is privileged — must go through server endpoint
  if (transitionName === 'transition/confirm-invoice-payment') {
    return transitionPrivileged({ isSpeculative: false, orderData: {}, bodyParams, queryParams })
      .then(response => {
        const entities = denormalisedResponseEntities(response);
        return entities[0];
      })
      .catch(e => rejectWithValue(storableError(e)));
  }

  return sdk.transactions
    .transition(bodyParams, queryParams)
    .then(response => {
      const entities = denormalisedResponseEntities(response);
      const order = entities[0];
      try {
        const { previousTransactionId: transactionId, eventListingId } = order.attributes.protectedData || {};
        // These bid-linking side effects only apply to the bid-acceptance checkout flow.
        // Rest payments / invoice payments have no previousTransactionId or eventListingId —
        // calling with undefined ids crashes transit serialization (e.stringRep is not a function).
        if (transactionId) {
          sdk.transactions.transition(
            {
              id: new UUID(transactionId),
              transition: "transition/accept-bid",
              params: {
                protectedData: {
                  paymentTransactionId: order.id.uuid,
                }
              }
            },
            { expand: true }
          ).catch(e => console.error('accept-bid side effect failed', e));
        }

        if (eventListingId) {
          sdk.ownListings.update({
            id: new UUID(eventListingId),
            publicData: {
              eventBid: EVENT_BID_CONFIRMED,
              defaultAcceptBid: "default-accept-bid",
              paymentTransactionId: order.id.uuid,
              bidTransactionId: transactionId,
            },
            privateData: {
              acceptedBidTransactionId: order.id.uuid,
              bidTransactionId: transactionId
            }
          }).catch(e => console.error('listing update side effect failed', e));
        }

        const { customer, provider } = order || {};
        const { uuid: customerId } = (customer && customer.id) || {};
        const { uuid: providerId } = (provider && provider.id) || {};

        if (transactionId) {
          crudTransactions({
            id: transactionId,
            action: 'update',
            transaction: {
              status: 'accepted',
              acceptedTxId: order.id.uuid,
            },
          }).catch(e => console.error('crudTransactions side effect failed', e));
        }

        // updateAlgoliaData({
        //   providerId,
        //   customerId,
        //   eventListingId,
        //   status: 'accepted',
        //   bidTxId: transactionId,
        //   objectID: eventListingId,
        //   acceptedTxId: order.id.uuid,
        //   indexName: process.env.REACT_APP_ALGOLIA_TRANSACTIONS_INDEX,
        // });
      } catch (e) {
        console.error(e, '&&& &&& => e');
      }
      return order;
    })
    .catch(e => {
      const transactionIdMaybe = transactionId ? { transactionId: transactionId.uuid } : {};
      log.error(e, 'initiate-order-failed', {
        ...transactionIdMaybe,
      });
      return rejectWithValue(storableError(e));
    });
};

export const confirmPaymentThunk = createAsyncThunk(
  'CheckoutPage/confirmPayment',
  confirmPaymentPayloadCreator
);
// Backward compatible wrapper function for confirmPayment
export const confirmPayment = (
  transactionId,
  transitionName,
  transitionParams = {}
) => dispatch => {
  return dispatch(
    confirmPaymentThunk({
      transactionId,
      transitionName,
      transitionParams,
    })
  ).unwrap();
};

//////////////////
// Send Message //
//////////////////
const sendMessagePayloadCreator = ({ message, id: orderId }, { extra: sdk, rejectWithValue }) => {
  if (message) {
    return sdk.messages
      .send({ transactionId: orderId, content: message })
      .then(() => {
        return { orderId, messageSuccess: true };
      })
      .catch(e => {
        log.error(e, 'initial-message-send-failed', { txId: orderId });
        return rejectWithValue({ orderId, messageSuccess: false });
      });
  } else {
    return { orderId, messageSuccess: true };
  }
};

export const sendMessageThunk = createAsyncThunk(
  'CheckoutPage/sendMessage',
  sendMessagePayloadCreator
);
// Backward compatible wrapper function for sendMessage
export const sendMessage = params => dispatch => {
  return dispatch(sendMessageThunk(params)).unwrap();
};

//////////////////////
// Initiate Inquiry //
//////////////////////
const initiateInquiryPayloadCreator = (
  { inquiryParams, processAlias, transitionName },
  { extra: sdk, rejectWithValue }
) => {
  if (!processAlias) {
    const error = new Error('No transaction process attached to listing');
    log.error(error, 'listing-process-missing', {
      listingId: inquiryParams?.listingId?.uuid,
    });
    return rejectWithValue(storableError(error));
  }

  const bodyParams = {
    transition: transitionName,
    processAlias,
    params: inquiryParams,
  };
  const queryParams = {
    include: ['provider'],
    expand: true,
  };

  return sdk.transactions
    .initiate(bodyParams, queryParams)
    .then(response => {
      const transactionId = response.data.data.id;
      return transactionId;
    })
    .catch(e => {
      return rejectWithValue(storableError(e));
    });
};

export const initiateInquiryThunk = createAsyncThunk(
  'CheckoutPage/initiateInquiry',
  initiateInquiryPayloadCreator
);
// Backward compatible wrapper function for initiateInquiryWithoutPayment
/**
 * Initiate transaction against default-inquiry process
 * Note: At this point inquiry transition is made directly against Marketplace API.
 *       So, client app's server is not involved here unlike with transitions including payments.
 *
 * @param {Object} params
 * @param {Object} params.inquiryParams contains listingId and protectedData
 * @param {String} params.processAlias 'default-inquiry/release-1'
 * @param {String} params.transitionName 'transition/inquire-without-payment'
 * @returns
 */
export const initiateInquiryWithoutPayment = (
  inquiryParams,
  processAlias,
  transitionName
) => dispatch => {
  return dispatch(
    initiateInquiryThunk({
      inquiryParams,
      processAlias,
      transitionName,
    })
  ).unwrap();
};

///////////////////////////
// Speculate Transaction //
///////////////////////////
/**
 * Initiate or transition the speculative transaction with the given
 * booking details
 *
 * The API allows us to do speculative transaction initiation and
 * transitions. This way we can create a test transaction and get the
 * actual pricing information as if the transaction had been started,
 * without affecting the actual data.
 *
 * We store this speculative transaction in the page store and use the
 * pricing info for the booking breakdown to get a proper estimate for
 * the price with the chosen information.
 */

const speculateTransactionPayloadCreator = (
  { orderParams, processAlias, transactionId, transitionName, isPrivilegedTransition },
  { dispatch, extra: sdk, rejectWithValue }
) => {
  // If we already have a transaction ID, we should transition, not initiate.
  const isTransition = !!transactionId;

  const {
    deliveryMethod,
    priceVariantName,
    quantity,
    bookingDates,
    ...otherOrderParams
  } = orderParams;

  const quantityMaybe = quantity ? { stockReservationQuantity: quantity } : {};

  const bookingParamsMaybe = bookingDates || {};

  // Parameters only for client app's server
  const orderData = {
    ...(deliveryMethod ? { deliveryMethod } : {}),
    ...(priceVariantName ? { priceVariantName } : {}),
    ...(otherOrderParams?.protectedData?.eventStartDateUnix ? { eventStartDateUnix: otherOrderParams.protectedData.eventStartDateUnix } : {})
  };

  // Parameters for Marketplace API
  const transitionParams = {
    ...quantityMaybe,
    ...bookingParamsMaybe,
    ...otherOrderParams,
    cardToken: 'CheckoutPage_speculative_card_token',
  };

  const bodyParams = isTransition
    ? {
      id: transactionId,
      transition: transitionName,
      params: transitionParams,
    }
    : {
      processAlias,
      transition: transitionName,
      params: transitionParams,
    };

  const queryParams = {
    include: ['booking', 'provider'],
    expand: true,
  };

  const handleSuccess = response => {
    const entities = denormalisedResponseEntities(response);
    if (entities.length !== 1) {
      throw new Error('Expected a resource in the speculate response');
    }
    const tx = entities[0];
    return tx;
  };

  const handleError = e => {
    log.error(e, 'speculate-transaction-failed', {
      listingId: transitionParams.listingId.uuid,
      ...quantityMaybe,
      ...bookingParamsMaybe,
      ...orderData,
      statusText: e.statusText,
    });
    return rejectWithValue(storableError(e));
  };

  if (isTransition) {
    // Always route speculative transitions through the privileged server endpoint.
    // Calling sdk.transactions.transitionSpeculative directly triggers a 403 when
    // Sharetribe enforces actor restrictions on the transition (e.g.
    // transition/request-payment-after-inquiry). The server endpoint uses the
    // Integration SDK (operator-level) which bypasses that restriction.
    return transitionPrivileged({ isSpeculative: true, orderData, bodyParams, queryParams })
      .then(handleSuccess)
      .catch(handleError);
  } else if (isPrivilegedTransition) {
    // initiate privileged
    return initiatePrivileged({ isSpeculative: true, orderData, bodyParams, queryParams })
      .then(handleSuccess)
      .catch(handleError);
  } else {
    // initiate non-privileged
    return sdk.transactions
      .initiateSpeculative(bodyParams, queryParams)
      .then(handleSuccess)
      .catch(handleError);
  }
};

export const speculateTransactionThunk = createAsyncThunk(
  'CheckoutPage/speculateTransaction',
  speculateTransactionPayloadCreator
);
// Backward compatible wrapper function for speculateTransaction
export const speculateTransaction = (
  orderParams,
  processAlias,
  transactionId,
  transitionName,
  isPrivilegedTransition
) => dispatch => {
  return dispatch(
    speculateTransactionThunk({
      orderParams,
      processAlias,
      transactionId,
      transitionName,
      isPrivilegedTransition,
    })
  ).unwrap();
};

///////////////////////////
// Fetch Stripe Customer //
///////////////////////////
const stripeCustomerPayloadCreator = ({ }, { dispatch, rejectWithValue }) => {
  const fetchCurrentUserOptions = {
    callParams: { include: ['stripeCustomer.defaultPaymentMethod'] },
    updateHasListings: false,
    updateNotifications: false,
    enforce: true,
  };

  return dispatch(fetchCurrentUser(fetchCurrentUserOptions))
    .then(response => {
      return response;
    })
    .catch(e => {
      return rejectWithValue(storableError(e));
    });
};

export const stripeCustomerThunk = createAsyncThunk(
  'CheckoutPage/stripeCustomer',
  stripeCustomerPayloadCreator
);
// Backward compatible wrapper function for stripeCustomer
export const stripeCustomer = () => dispatch => {
  return dispatch(stripeCustomerThunk({})).unwrap();
};

/////////////////////////
// Bid Initiate Order //
///////////////////////
const bidInitiateOrderPayloadCreator = (
  { orderParams, processAlias, requestTransition },
  { dispatch, extra: sdk, rejectWithValue }
) => {
  try {
    const quantity = 1;
    const { eventListing, businessListing, eventListingId, businessListingId, eventStartDateUnix, eventEndDateUnix, bidData, ...rest } = orderParams;

    const { adminProviderCommissionMaybe = false, adminCustomerCommisionMaybe = false } = bidData;

    const bodyParams = {
      processAlias,
      transition: requestTransition,
      params: {
        stockReservationQuantity: quantity,
        listingId: eventListingId,
        protectedData: {
          ...rest,
          eventListing, businessListing, eventListingId, businessListingId, eventStartDateUnix, eventEndDateUnix,
          bid: {
            price: bidData.offer.amount,
            currency: bidData.offer.currency,
            note: (bidData?.note || ""),
            tax: (bidData?.tax?.amount || 0),
            adminCustomerCommisionMaybe,
            adminProviderCommissionMaybe
          }
        },
      },
    };

    const queryParams = {
      include: ['booking', 'customer', 'provider', 'listing'],
      expand: true,
    };

    const handleError = e => {
      dispatch(submitBidError(storableError(e)));
      const transactionIdMaybe = {};
      log.error(e, 'initiate-order-failed', {
        ...transactionIdMaybe,
        quantityMaybe: quantity,
        ...orderData,
      });
      throw e;
    };

    const orderData = { quantity, bidInitiate: true, bidData: { ...bidData, tax: bidData?.tax?.amount } };

    return initiatePrivileged({ isSpeculative: false, orderData, bodyParams, queryParams })
      .then(res => {
        const entities = denormalisedResponseEntities(res);
        const order = entities[0];
        const { attributes, customer, provider, listing } = order || {};
        const { payinTotal, protectedData, metadata } = attributes || {};
        const { note, tax, price, currency } = { ...protectedData.bid, ...metadata };
        const { uuid: providerId } = (customer && customer.id) || {};
        const { uuid: customerId } = (provider && provider.id) || {};

        const { city, venueAddress } = (listing && listing.id && listing.attributes.publicData) || {};
        const bidTotalAmount = ((bidData.offer.amount) + (bidData.tax && bidData.tax.amount ? bidData.tax.amount : 0));

        const algoliaObject = {
          city,
          customerId,
          providerId,
          eventListingId,
          status: 'bid',
          acceptedTxId: '',
          bidTxId: order.id.uuid,
          note, tax, price, currency,
          bidPrice: bidTotalAmount,
        };

        if (venueAddress && venueAddress.selectedPlace && venueAddress.selectedPlace.origin) {
          const distancePromisify = [], serviceRadiuses = [];
          const { publicData } = (providerId && customer.attributes.profile) || {};
          const { zipCodeRadius } = publicData || {};
          if (zipCodeRadius && zipCodeRadius.length) {
            zipCodeRadius.map(zipCode => {
              const { origin, primaryZip, serviceRadius } = zipCode;

              if (origin && origin.lat) {
                serviceRadiuses.push({ zipCode: primaryZip, id: providerId, serviceRadius });
                distancePromisify.push(getDistanceOfUser({
                  customerOrigin: origin,
                  zipCode: primaryZip,
                  id: providerId,
                  listingOrigin: venueAddress.selectedPlace.origin,
                }));
              }
            });
          }

          if (distancePromisify && distancePromisify.length) {
            Promise.all(distancePromisify)
              .then(distancePromisified => {
                if (distancePromisified && distancePromisified.length) {
                  distancePromisified
                    .sort((a, b) => a.distanceMeter - b.distanceMeter)
                    .filter(p => {
                      const index = serviceRadiuses.findIndex(sr => sr.id == p.id && sr.zipCode == p.zipCode && (p.distanceMeter == 0 || (p.distanceMeter && sr.serviceRadius && sr.serviceRadius >= p.distanceMeter)));
                      if (index > -1) {
                        Object.assign(algoliaObject, {
                          distanceFromCustomer: (p.distanceMile ? p.distanceMile : 0)
                        });
                        return p;
                      }
                    });
                }
                crudTransactions({
                  action: 'create',
                  transaction: {
                    id: order.id.uuid,
                    ...algoliaObject
                  },
                });
                createAlgoliaData({
                  ...algoliaObject,
                  objectID: eventListingId,
                  indexName: process.env.REACT_APP_ALGOLIA_TRANSACTIONS_INDEX,
                });
              });
          }
        } else {
          crudTransactions({
            action: 'create',
            transaction: {
              id: order.id.uuid,
              ...algoliaObject
            },
          });
          createAlgoliaData({
            ...algoliaObject,
            objectID: eventListingId,
            indexName: process.env.REACT_APP_ALGOLIA_TRANSACTIONS_INDEX,
          });
        }

        // Increase the bid on RFP count in the provider business listing
        const { currentUser } = getState().user;
        const { protectedData: uProtectedData } = (currentUser && currentUser.id && currentUser.attributes.profile) || {};
        const { businessListingId = '', } = uProtectedData || {};
        const ref = { id: new UUID(businessListingId), type: 'ownListing' };
        const businessListings = getMarketplaceEntities(getState(), [ref]);
        const businessListing = businessListings.length ? ensureOwnListing(businessListings[0]) : ensureOwnListing({});
        if (businessListing && businessListing.id) {
          const { bidOnRFPs = 0, } = businessListing.attributes.publicData || {};
          sdk.ownListings.update({
            publicData: {
              bidOnRFPs: (bidOnRFPs + 1)
            }
          });
        }

        onUpdateEventBidListing({
          eventListingId,
          transactionId: order.id.uuid,
        });
        // dispatch(bidInitiateOrderSuccess(order));
        return order;
      })
      .catch(handleError);
  } catch (e) {
    return dispatch(submitBidError(storableError(e)));
  }
}


export const bidInitiateOrderThunk = createAsyncThunk(
  'CheckoutPage/biddingOrder',
  bidInitiateOrderPayloadCreator
);
// Backward compatible wrapper function for stripeCustomer
export const bidInitiateOrder = (orderParams, processAlias, requestTransition) => dispatch => {
  return dispatch(bidInitiateOrderThunk({ orderParams, processAlias, requestTransition })).unwrap();
};


// ================ Slice ================ //

const initialState = {
  listing: null,
  orderData: null,
  speculateTransactionInProgress: false,
  speculateTransactionError: null,
  speculatedTransaction: null,
  isClockInSync: false,
  transaction: null,
  initiateOrderError: null,
  confirmPaymentError: null,
  stripeCustomerFetched: false,
  initiateInquiryInProgress: false,
  initiateInquiryError: null,
  submitBidInProgress: false,
  submitBidData: null,
  submitBidError: null,
};

const checkoutPageSlice = createSlice({
  name: 'CheckoutPage',
  initialState,
  reducers: {
    setInitialValues: (state, action) => {
      return { ...initialState, ...pick(action.payload, Object.keys(initialState)) };
    },
  },
  extraReducers: builder => {
    builder
      // Initiate Order cases
      .addCase(initiateOrderThunk.pending, state => {
        state.initiateOrderError = null;
      })
      .addCase(initiateOrderThunk.fulfilled, (state, action) => {
        state.transaction = action.payload;
      })
      .addCase(initiateOrderThunk.rejected, (state, action) => {
        console.error(action.payload); // eslint-disable-line no-console
        state.initiateOrderError = action.payload;
      })
      // Confirm Payment cases
      .addCase(confirmPaymentThunk.pending, state => {
        state.confirmPaymentError = null;
      })
      .addCase(confirmPaymentThunk.fulfilled, state => {
        // Payment confirmed successfully, no state change needed
      })
      .addCase(confirmPaymentThunk.rejected, (state, action) => {
        console.error(action.payload); // eslint-disable-line no-console
        state.confirmPaymentError = action.payload;
      })
      // Speculate Transaction cases
      .addCase(speculateTransactionThunk.pending, state => {
        state.speculateTransactionInProgress = true;
        state.speculateTransactionError = null;
        state.speculatedTransaction = null;
      })
      .addCase(speculateTransactionThunk.fulfilled, (state, action) => {
        // Check that the local devices clock is within a minute from the server
        const lastTransitionedAt = action.payload?.attributes?.lastTransitionedAt;
        const localTime = new Date();
        const minute = 60000;
        state.speculateTransactionInProgress = false;
        state.speculatedTransaction = action.payload;
        state.isClockInSync =
          Math.abs(lastTransitionedAt?.getTime() - localTime.getTime()) < minute;
      })
      .addCase(speculateTransactionThunk.rejected, (state, action) => {
        console.error(action.payload); // eslint-disable-line no-console
        state.speculateTransactionInProgress = false;
        state.speculateTransactionError = action.payload;
      })
      // Stripe Customer cases
      .addCase(stripeCustomerThunk.pending, state => {
        state.stripeCustomerFetched = false;
      })
      .addCase(stripeCustomerThunk.fulfilled, state => {
        state.stripeCustomerFetched = true;
      })
      .addCase(stripeCustomerThunk.rejected, (state, action) => {
        console.error(action.payload); // eslint-disable-line no-console
        state.stripeCustomerFetchError = action.payload;
      })
      // Initiate Inquiry cases
      .addCase(initiateInquiryThunk.pending, state => {
        state.initiateInquiryInProgress = true;
        state.initiateInquiryError = null;
      })
      .addCase(initiateInquiryThunk.fulfilled, state => {
        state.initiateInquiryInProgress = false;
      })
      .addCase(initiateInquiryThunk.rejected, (state, action) => {
        state.initiateInquiryInProgress = false;
        state.initiateInquiryError = action.payload;
      });
  },
});

// Export the action creators
export const { setInitialValues } = checkoutPageSlice.actions;

// Export the reducer
export default checkoutPageSlice.reducer;